import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import {
    filter,
    HookMatchCriteria,
    Transition,
    TransitionHookFn,
    TransitionService
} from '@uirouter/core';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthenticationService } from './auth/authentication.service';
import { UnsavedChangesService } from './shared/modal-dialog';
import * as fromShared from './shared';
import { ILoggingService } from './shared/services/logging/logging.service';
import * as fromRoot from './store';
import * as fromDataServices from './data-services';
import * as fromAuth from './auth/store';
import * as fromUserInfo from './user-info';
import * as fromUserRole from './user-role';
import * as fromUserSetting from './user-setting';
import { RegistrationModel } from './data-services/registration';
import { TimecardUnsavedStateService } from './timecard/timecard-unsaved-state.service';

/**
 * This file contains a Transition Hook which protects a
 * route that requires authentication.
 *
 * This hook redirects to /login when both:
 * - The user is not authenticated
 * - The user is navigating to a state that requires authentication
 */
export function routerHooks(
    transitionService: TransitionService,
    log: ILoggingService,
    store: Store<fromRoot.State>,
    entityServices: EntityServices
) {
    const registrationDataService =
        entityServices.getEntityCollectionService<fromDataServices.Registration.RegistrationModel>(
            fromDataServices.Registration.entityName
        );
    // Matches if the destination state's data property has a truthy 'requiresAuth' property
    const requiresAuthCriteria: HookMatchCriteria = {
        to: state => state?.data && state.data.requiresAuth
    };

    const clearErrorsCriteria: HookMatchCriteria = {
        entering: '*'
    };

    // Function that returns a redirect for the current transition to the login state
    // if the user is not currently authenticated (according to the AuthService)

    const redirectToLogin: TransitionHookFn = (transition: Transition) => {
        const authService: AuthenticationService = transition
            .injector()
            .get(AuthenticationService);
        // log.verbose({
        //     type: 'routerHooks:redirectToLogin',
        //     authenticated: authService.isAuthenticated()
        // });
        const $state = transition.router.stateService;
        if (!authService.isAuthenticated()) {
            authService.openDialog();
            // return $state.target('main', undefined, { location: false });
        }
    };

    const clearErrors: TransitionHookFn = (transition: Transition) => {
        // log.verbose({ type: 'routerHooks:clearErrors', transition });
        store.dispatch(new fromShared.Errors.ClearErrors());
    };

    /** When leaving timecard, warn if there are unsaved in-memory timecard changes. */
    const timecardPendingGuard: TransitionHookFn = (transition: Transition) => {
        const timecardUnsavedStateService = transition
            .injector()
            .get(TimecardUnsavedStateService);
        if (!timecardUnsavedStateService.getHasPendingTimecardChanges()) return;

        const unsavedService = transition.injector().get(UnsavedChangesService);
        return unsavedService
            .confirmPromise({
                dialogText:
                    'You have unsaved time entries. Select Leave to DISCARD changes or Stay to continue editing.',
                okText: 'Leave',
                cancelText: 'Stay'
            })
            .then(() => {})
            .catch(() => Promise.reject(new Error('User chose to stay')));
    };

    const requiresTimecardPendingGuard: HookMatchCriteria = {
        from: state =>
            state?.name === 'timecard' ||
            (state?.name && state.name.startsWith('timecard.'))
    };

    const hasAccess: TransitionHookFn = (t: Transition) => {
        combineLatest([
            store.pipe(select(fromUserInfo.getUserProcessCategories)),
            store.pipe(select(fromUserInfo.getUserProcessesMain))
        ])
            .pipe(take(1))
            .subscribe(upc => {
                const processes: fromShared.Process[] = upc[0]
                    .reduce(
                        (a, c) => a.concat(c.UserProcesses),
                        [] as fromShared.Process[]
                    )
                    .concat([
                        {
                            ProcessID: 0,
                            ProcessName: 'Main',
                            RouteName: 'main',
                            ProcessDesc: '',
                            ProcessTitle: ''
                        },
                        {
                            ProcessID: -1,
                            ProcessName: 'Receipts',
                            RouteName: 'receipt',
                            ProcessDesc: '',
                            ProcessTitle: ''
                        },
                        {
                            ProcessID: -2,
                            ProcessName: 'Attachment',
                            RouteName: 'attachment',
                            ProcessDesc: '',
                            ProcessTitle: ''
                        },
                        {
                            ProcessID: -3,
                            ProcessName: 'Dashboard',
                            RouteName: 'dashboard',
                            ProcessDesc: 'Dashboard',
                            ProcessTitle: 'Dashboard'
                        },
                        {
                            ProcessID: -4,
                            ProcessName: 'qbrevoke',
                            RouteName: 'qbrevoke',
                            ProcessDesc: 'Revoke QuickBooks access',
                            ProcessTitle: 'Revoke QuickBooks Access'
                        },
                        {
                            ProcessID: -5,
                            ProcessName: 'quickbooksauth',
                            RouteName: 'quickbooksauth',
                            ProcessDesc: '',
                            ProcessTitle: ''
                        },
                        {
                            ProcessID: -6,
                            ProcessName: 'accessgranted',
                            RouteName: 'quickbooks.accessgranted',
                            ProcessDesc: 'Access Granted',
                            ProcessTitle: 'Access Granted'
                        },
                        {
                            ProcessID: -7,
                            ProcessName: 'register',
                            RouteName: 'register',
                            ProcessDesc: 'Registration',
                            ProcessTitle: 'Registration'
                        },
                        {
                            ProcessID: -8,
                            ProcessName: 'Period Processing',
                            RouteName: 'period.reprocessing',
                            ProcessDesc: 'Period Processing',
                            ProcessTitle: 'Period Processing'
                        },
                        {
                            ProcessID: -9,
                            ProcessName: 'Task Timer',
                            RouteName: 'tasktimer',
                            ProcessDesc: 'Task Timer',
                            ProcessTitle: 'Task Timer'
                        },
                        {
                            ProcessID: -10,
                            ProcessName: 'Internal Admin',
                            RouteName: 'admin',
                            ProcessDesc: 'Internal Admin',
                            ProcessTitle: 'Internal Admin'
                        },
                        {
                            ProcessID: -11,
                            ProcessName: 'eFAACT Store',
                            RouteName: 'efaactstore',
                            ProcessDesc: 'eFAACT Store',
                            ProcessTitle: 'eFAACT Store'
                        },
                        {
                            ProcessID: -12,
                            ProcessName: 'About',
                            RouteName: 'about',
                            ProcessDesc: 'About',
                            ProcessTitle: 'About'
                        },
                        {
                            ProcessID: -13,
                            ProcessName: 'Employee Validation',
                            RouteName: 'employeevalidation',
                            ProcessDesc: 'Employee Validation',
                            ProcessTitle: 'Employee Validation'
                        },
                        {
                            ProcessID: -12,
                            ProcessName: 'qbaccessrevoked',
                            RouteName: 'qbaccessrevoked',
                            ProcessDesc: 'QB Access Revoked',
                            ProcessTitle: 'QB Access Revoked'
                        },
                        {
                            ProcessID: -13,
                            ProcessName: 'Password Change',
                            RouteName: 'passwordchange',
                            ProcessDesc: 'Password change is required for first time users, or when you are sent a temporary password via email.',
                            ProcessTitle: 'Password Change'
                        },
                        {
                            ProcessID: -14,
                            ProcessName: 'eFAACT Expert',
                            RouteName: 'efaactexpert',
                            ProcessDesc: 'eFAACT Expert',
                            ProcessTitle: 'eFAACT Expert'
                        }
                        // {
                        //     ProcessID: -10,
                        //     ProcessName: 'Internal Admin',
                        //     RouteName: 'admin',
                        //     ProcessDesc: 'Internal Admin',
                        //     ProcessTitle: 'Internal Admin'
                        // }
                        // {
                        //     ProcessID: -99,
                        //     ProcessName: 'vendor',
                        //     RouteName: 'vendor',
                        //     ProcessDesc: 'Vendor',
                        //     ProcessTitle: 'Vendor'
                        // }
                    ])
                    .concat(upc[1])
                    .sort((a, b) => a.ProcessID - b.ProcessID);
                log.verbose({
                    type: 'routerHooks:hasAccess',
                    processes,
                    upc,
                    store,
                    toState: t.targetState().name()
                });
                const process =
                    processes &&
                    processes.find(p => {
                        if (p.RouteName) {
                            return (
                                p.RouteName.toLocaleLowerCase().replace(
                                    '/',
                                    '.'
                                ) === t.targetState().name().toLocaleLowerCase()
                            );
                        }
                    });
                log.verbose({
                    type: 'routerHooks:hasAccess',
                    process
                });
                // return !!process;
                // if (environment.production !== false) {
                if (!process) {
                    t.router.stateService.go('main');
                }
                // }
            });
    };

    const needsRegistration: TransitionHookFn = (t: Transition) => {
        combineLatest([
            store.pipe(select(fromUserRole.getUserRoles)),
            store.pipe(select(fromAuth.getAuth)),
            registrationDataService.entities$
        ])
            .pipe(take(1))
            .subscribe(([roles, auth, registrations]) => {
                if (t.targetState().name().toLocaleLowerCase() !== 'register') {
                    log.verbose({
                        type: 'routerHooks:needsRegistration',
                        roles,
                        auth,
                        registrations,
                        store,
                        toState: t.targetState().name()
                    });
                    if (
                        roles.some(
                            x =>
                                x.RoleGroupID ===
                                fromUserInfo.UserRoleEnum.Administrator
                        )
                    ) {
                        log.verbose({
                            type: 'routerHooks:needsRegistration: user is admin',
                            roles
                        });
                        const registration = registrations.find(
                            x => x.CustomerId === auth.CustomerID
                        );
                        log.verbose({
                            type: 'routerHooks:needsRegistration: registration',
                            registration
                        });
                        if (registration?.NeedsRegistration) {
                            log.verbose({
                                type: 'routerHooks:needsRegistration: registration needed',
                                registration
                            });
                            t.abort();
                            t.router.stateService.go('register');
                        }
                    }
                }
            });
    };

    const temporaryPassword: TransitionHookFn = (t: Transition) => {
        store
            .pipe(
                select(
                    fromUserSetting.getSettingBooleanValue('TemporaryPassword')
                )
            )
            .subscribe(setting => {
                log.verbose({
                    type: 'routerHooks:temporaryPassword',
                    setting
                });
                if (setting) {
                    if (t.targetState().name() !== 'passwordchange') {
                        //use UI-router change the state to temporarypassword
                        t.abort();
                        t.router.stateService.go('passwordchange');
                    }
                }
            });
    };

    // Register the "requires auth" hook with the TransitionsService
    transitionService.onBefore(requiresTimecardPendingGuard, timecardPendingGuard, {
        priority: 2
    });
    transitionService.onBefore(requiresAuthCriteria, redirectToLogin, {
        priority: 1
    });
    transitionService.onBefore(requiresAuthCriteria, temporaryPassword, {
        priority: 9
    });
    transitionService.onBefore(requiresAuthCriteria, needsRegistration, {
        priority: 10
    });
    transitionService.onEnter(requiresAuthCriteria, hasAccess, {
        priority: 11
    });
    transitionService.onEnter(clearErrorsCriteria, clearErrors, {
        priority: 12
    });

    transitionService.onEnter(
        { entering: '*' },
        (t: Transition) => {
            store
                .pipe(select(fromUserInfo.getUserProcessCategories), take(1))
                .subscribe(upc => {
                    const processes: fromShared.Process[] = upc.reduce(
                        (a, c) => a.concat(c.UserProcesses),
                        []
                    );
                    log.verbose({
                        type: 'routerHooks:process',
                        toState: t.targetState().name(),
                        processes,
                        upc,
                        store
                    });
                    const process =
                        processes &&
                        processes.find(p => {
                            if (p.RouteName) {
                                return (
                                    p.RouteName.toLocaleLowerCase().replace(
                                        '/',
                                        '.'
                                    ) ===
                                    t.targetState().name().toLocaleLowerCase()
                                );
                            }
                        });
                    if (process) {
                        store.dispatch(fromShared.clearProcess());
                        store.dispatch(fromShared.upsertProcess({ process }));
                    }
                });
        },
        { priority: 99 }
    );

    transitionService.onError({ to: state => state?.name !== 'main' }, t => {
        // const authService: AuthenticationService = transition
        //     .injector()
        //     .get(AuthenticationService);
        // log.verbose({
        //     type: 'routerHooks:onError',
        //     authenticated: authService.isAuthenticated()
        // });
        // if (authService.isAuthenticated()) {
        // t.router.stateService.go('main');
        // }
    });
}
